import { api } from 'services/api';

const petOwnerService = api
  .enhanceEndpoints({
    addTagTypes: [
      'NotificationSettings',
      'SubscriptionSettings',
      'ChangeMonthlySubscriptionCreditCard',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      login: builder.mutation<ResultModel<LoginResponse>, LoginRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/Login`,
          method: 'POST',
          body,
        }),
      }),
      welcome: builder.mutation<ResultModel<WelcomeResponse>, WelcomeRequest>({
        query: (body) => ({
          url: `/welcome`,
          method: 'POST',
          body,
        }),
      }),
      insert: builder.mutation<ResultModel<InsertResponse>, InsertRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/Insert`,
          method: 'POST',
          body,
        }),
      }),
      verify: builder.mutation<ResultModel<VerifyResponse>, VerifyRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/Verify`,
          method: 'POST',
          body,
        }),
        extraOptions: { isShowErrorMessage: false },
      }),
      verifyInsert: builder.mutation<ResultModel<VerifyInsertResponse>, VerifyInsertRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/VerifyInsert`,
          method: 'POST',
          body,
        }),
        extraOptions: { isShowErrorMessage: false },
      }),
      petOwnerDetail: builder.query<ResultModel<PetOwnerDetail>, void>({
        query: (body) => ({
          url: `/4437769E28D6711C/Detail`,
          method: 'POST',
          body,
        }),
        extraOptions: { isProfile: true },
      }),
      petOwnerDetailForIys: builder.mutation<ResultModel<PetOwnerDetail>, { profile: UserModel }>({
        query: (body) => ({
          url: `/4437769E28D6711C/Detail`,
          method: 'POST',
          body,
        }),
      }),
      addIysPermission: builder.mutation<ResultModel<void>, AddIysPermissionRequest>({
        query: (body) => ({
          url: `/5D2B71F27C9B37/AddRegisteredPersonToWhiteListWithoutSmsVerification`,
          method: 'POST',
          body,
        }),
        extraOptions: { isProfile: true },
      }),
      updateIysPermission: builder.mutation<ResultModel<void>, UpdateIysPermissionRequest>({
        query: (body) => ({
          url: `/5D2B71F27C9B37/ReAddPersonToWhiteListWithoutSmsVerification`,
          method: 'POST',
          body,
        }),
      }),
      getKPS: builder.query<ResultModel<KPSResponse>, KPSRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/IdentitySharingSystemQuery`,
          method: 'POST',
          body,
        }),
      }),
      kocAilem: builder.query<ResultModel<KocAilemResponse>, void>({
        query: (body) => ({
          url: `/4437769E28D6711C/KocAilem/QueryMembership`,
          method: 'POST',
          body,
        }),
        extraOptions: { isProfile: true, isShowErrorMessage: false },
      }),
      profilePhoto: builder.mutation<ResultModel<PetOwnerDetail>, ChangeProfilePhotoRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/ChangeProfilePhoto`,
          method: 'POST',
          body,
        }),
        extraOptions: { isProfile: true },
        onQueryStarted({ data: { photo, isDelete } }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            petOwnerService.util.updateQueryData('petOwnerDetail', undefined, (result) => {
              if (result.isSuccess) {
                result.data.photo = isDelete ? '' : photo;
              }
            })
          );
          queryFulfilled.catch(patchResult.undo);
        },
      }),
      // prettier-ignore
      notificationSettings: builder.query<ResultModel<NotificationSettingsResponse[]>, NotificationSettingsRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/NotificationSettings`,
          method: 'POST',
          body,
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'NotificationSettings' as const, id })),
                { type: 'NotificationSettings', id: 'LIST' },
              ]
            : [{ type: 'NotificationSettings', id: 'LIST' }],
        extraOptions: { isProfile: true },
      }),
      // prettier-ignore
      updateNotificationSettings: builder.mutation<ResultModel<void>, UpdateNotificationSettingsRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/UpdateNotificationSettings`,
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'NotificationSettings', id: 'LIST' }],
        extraOptions: { isProfile: true },
      }),
      // prettier-ignore
      changeLanguage: builder.mutation<ResultModel<void>, ChangeLanguageRequest>({
        query: (body) => ({
          url: `/4437769E28D6711C/ChangeLanguage`,
          method: 'POST',
          body,
        }),
        extraOptions: { isProfile: true },

      }),
      subscriptionSettings: builder.query<
        ResultModel<SubscriptionSettings>,
        SubscriptionSettingsRequest
      >({
        query: (body) => ({
          url: `/4437769E28D6711C/SubscriptionSettings`,
          method: 'POST',
          body,
        }),
        providesTags: [{ type: 'SubscriptionSettings', id: 'LIST' }],
        extraOptions: { isProfile: true },
      }),
    }),
  });

export const {
  useLoginMutation,
  useWelcomeMutation,
  useInsertMutation,
  useVerifyMutation,
  useVerifyInsertMutation,
  usePetOwnerDetailQuery,
  usePetOwnerDetailForIysMutation,
  useAddIysPermissionMutation,
  useUpdateIysPermissionMutation,
  useGetKPSQuery,
  useKocAilemQuery,
  useProfilePhotoMutation,
  useNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useChangeLanguageMutation,
  useSubscriptionSettingsQuery,
} = petOwnerService;

